import { useState } from 'react'
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react'
import { clsx } from 'clsx'
import { ProjectMargin } from '../../hooks/useKPIs'

interface MarginProjectsTableProps {
  projects: ProjectMargin[]
}

type SortKey = 'margin_pct' | 'completion_pct' | 'burn_rate_pct' | 'eac'
type SortDir = 'asc' | 'desc'

function formatEur(v: number): string {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(v)
}

function marginStatus(pct: number): {
  label: string
  chip: string
} {
  if (pct >= 30) return { label: `${pct.toFixed(1)}%`, chip: 'bg-emerald-500/15 text-emerald-400' }
  if (pct >= 15) return { label: `${pct.toFixed(1)}%`, chip: 'bg-amber-500/15 text-amber-400' }
  if (pct >= 0)  return { label: `${pct.toFixed(1)}%`, chip: 'bg-orange-500/15 text-orange-400' }
  return         { label: `${pct.toFixed(1)}%`, chip: 'bg-red-500/15 text-red-400' }
}

function burnStatus(burnPct: number, completionPct: number): string {
  const delta = burnPct - completionPct
  if (delta > 15)  return 'text-red-400'
  if (delta > 5)   return 'text-amber-400'
  return 'text-[--km-text-primary]'
}

// Barra di completamento
function CompletionBar({ pct }: { pct: number }) {
  const clamped = Math.min(Math.max(pct, 0), 100)
  return (
    <div className="flex items-center gap-2 min-w-[80px]">
      <div className="flex-1 h-1.5 rounded-full bg-[--km-surface-3] overflow-hidden">
        <div
          className="h-full rounded-full bg-[#6366f1] transition-all duration-300"
          style={{ width: `${clamped}%` }}
        />
      </div>
      <span className="text-xs tabular-nums text-[--km-text-secondary] w-9 text-right">
        {pct.toFixed(0)}%
      </span>
    </div>
  )
}

function SortIcon({ col, active, dir }: { col: SortKey; active: SortKey; dir: SortDir }) {
  if (col !== active) return <ArrowUpDown size={12} className="text-[--km-text-muted]" />
  return dir === 'asc'
    ? <ArrowUp size={12} className="text-[--km-primary]" />
    : <ArrowDown size={12} className="text-[--km-primary]" />
}

function EmptyState() {
  return (
    <div className="py-12 text-center">
      <p className="text-sm text-[--km-text-muted]">Nessuna commessa attiva trovata.</p>
    </div>
  )
}

export function MarginProjectsTable({ projects }: MarginProjectsTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>('margin_pct')
  const [sortDir, setSortDir] = useState<SortDir>('asc')

  function handleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const sorted = [...projects].sort((a, b) => {
    const va = a[sortKey]
    const vb = b[sortKey]
    return sortDir === 'asc' ? va - vb : vb - va
  })

  const COLS: { key: SortKey; label: string }[] = [
    { key: 'margin_pct',     label: 'Margine %' },
    { key: 'completion_pct', label: 'Avanzamento' },
    { key: 'burn_rate_pct',  label: 'Burn rate' },
    { key: 'eac',            label: 'EAC' },
  ]

  return (
    <div
      className="rounded-xl border border-[--km-border] bg-[--km-surface] overflow-hidden"
      data-testid="margin-projects-table"
    >
      {/* Header */}
      <div className="px-5 py-4 border-b border-[--km-border]">
        <p className="text-xs font-medium uppercase tracking-widest text-[--km-text-secondary]">
          Commesse attive — drill-down
        </p>
      </div>

      {projects.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          {/* Mobile card view (< sm) */}
          <div className="sm:hidden divide-y divide-[--km-border]">
            {sorted.map((p) => {
              const ms = marginStatus(p.margin_pct)
              const burnCls = burnStatus(p.burn_rate_pct, p.completion_pct)
              return (
                <div
                  key={p.order_id}
                  className={`px-4 py-4 space-y-3 ${p.margin_pct < 0 ? 'bg-red-500/[0.03]' : ''}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-[--km-text-primary] text-sm leading-tight">{p.customer_name}</p>
                      {p.service_line && (
                        <p className="text-xs text-[--km-text-muted] mt-0.5">{p.service_line}</p>
                      )}
                    </div>
                    <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${ms.chip}`}>
                      {ms.label}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                    <div>
                      <p className="text-[--km-text-muted] mb-0.5">Valore ordine</p>
                      <p className="text-[--km-text-secondary] tabular-nums font-medium">{formatEur(p.order_value)}</p>
                    </div>
                    <div>
                      <p className="text-[--km-text-muted] mb-0.5">Burn rate</p>
                      <p className={`tabular-nums font-medium ${burnCls}`}>{p.burn_rate_pct.toFixed(0)}%
                        {p.burn_rate_pct - p.completion_pct > 5 && (
                          <span className="ml-1 text-red-400">over budget</span>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-[--km-text-muted] mb-1">Avanzamento</p>
                      <CompletionBar pct={p.completion_pct} />
                    </div>
                    <div>
                      <p className="text-[--km-text-muted] mb-0.5">EAC</p>
                      <p className={`tabular-nums font-medium ${p.eac > p.order_value ? 'text-red-400' : 'text-[--km-text-secondary]'}`}>
                        {formatEur(p.eac)}
                        {p.eac > p.order_value && <span className="block text-[10px] text-red-400">supera ordine</span>}
                      </p>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Desktop table view (>= sm) */}
          <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-sm" aria-label="Tabella commesse">
            <thead>
              <tr className="border-b border-[--km-border]">
                <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-[--km-text-muted]">
                  Commessa
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[--km-text-muted]">
                  Valore ordine
                </th>
                {COLS.map(({ key, label }) => (
                  <th key={key} className="px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort(key)}
                      className="flex items-center gap-1 text-xs font-medium uppercase tracking-wider text-[--km-text-muted] hover:text-[--km-text-secondary] transition-colors"
                      aria-label={`Ordina per ${label}`}
                    >
                      {label}
                      <SortIcon col={key} active={sortKey} dir={sortDir} />
                    </button>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-[--km-border]">
              {sorted.map((p) => {
                const ms = marginStatus(p.margin_pct)
                return (
                  <tr
                    key={p.order_id}
                    className={clsx(
                      'hover:bg-[--km-surface-2] transition-colors',
                      p.margin_pct < 0 && 'bg-red-500/[0.03]',
                    )}
                  >
                    {/* Commessa */}
                    <td className="px-5 py-3">
                      <p className="font-medium text-[--km-text-primary] truncate max-w-[200px]">
                        {p.customer_name}
                      </p>
                      {p.service_line && (
                        <p className="text-xs text-[--km-text-muted] mt-0.5">{p.service_line}</p>
                      )}
                    </td>

                    {/* Valore ordine */}
                    <td className="px-4 py-3 tabular-nums text-[--km-text-secondary]">
                      {formatEur(p.order_value)}
                    </td>

                    {/* Margine % chip */}
                    <td className="px-4 py-3">
                      <span className={clsx('rounded-full px-2.5 py-0.5 text-xs font-semibold', ms.chip)}>
                        {ms.label}
                      </span>
                    </td>

                    {/* Avanzamento barra */}
                    <td className="px-4 py-3">
                      <CompletionBar pct={p.completion_pct} />
                    </td>

                    {/* Burn rate */}
                    <td className="px-4 py-3">
                      <span className={clsx('text-sm font-medium tabular-nums', burnStatus(p.burn_rate_pct, p.completion_pct))}>
                        {p.burn_rate_pct.toFixed(0)}%
                      </span>
                      {p.burn_rate_pct - p.completion_pct > 5 && (
                        <p className="text-[10px] text-red-400 mt-0.5">over budget</p>
                      )}
                    </td>

                    {/* EAC */}
                    <td className="px-4 py-3">
                      <span
                        className={clsx(
                          'tabular-nums text-sm',
                          p.eac > p.order_value ? 'text-red-400 font-semibold' : 'text-[--km-text-secondary]',
                        )}
                      >
                        {formatEur(p.eac)}
                      </span>
                      {p.eac > p.order_value && (
                        <p className="text-[10px] text-red-400 mt-0.5">supera ordine</p>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          </div>
        </>
      )}
    </div>
  )
}
